import { createSelector } from 'reselect'
import TypeContrat from '../../../constant/TypeContrat'
import FormatUtils from '../../../utils/FormatUtils'
import { getAffiliationAssurePrincipal, getAffiliationBeneficiairesForManage, getTousAssuresPrincipaux } from '../affiliation/affiliationSelector'

const getContratsByCompanies = state => state.contrats.data && state.contrats.data
export const isContratsLoading = state => state.contrats.loading
export const getExpirationDate = state => state.contrats.expirationDate

const transformGarantie = g => ({
	...g,
	typeGar: g.typeGar === 'Garantie optionnelle' ? 'Garantie santé optionnelle' : 'Garantie Santé'
})

const selectGarantieToDisplay = (g) => {
	// on affiche uniquement les garanties qui portent des prestations
	if (g.deleg !== 2 && g.deleg !== 3) {
		return false
	}
	return validateDateFin(g.dateFin)
}

const validateDateFin = date => {
	if (date) {
		const dateFin = new Date(FormatUtils.toLocalDate(date))
		const dateJour = new Date()
		return dateFin >= dateJour
	}
	return true
}

const getContratsSanteByCompanies = createSelector(
	[getContratsByCompanies], contrats => (
		contrats.filter(contrat => (
			contrat.nature === TypeContrat.SANTE || contrat.nature === TypeContrat.SANTE_PREVOYANCE
		))
	)
)

/**
 * Le contrat santé de base de l'assuré principal.
 */
export const getContratBaseSante = createSelector([
	getTousAssuresPrincipaux,
	getContratsSanteByCompanies
], (affiliationsAssurePrincipal, contrats) => {
	if (affiliationsAssurePrincipal.length > 0 && contrats.length > 0) {
		const contratBase = affiliationsAssurePrincipal
			.map((affiliationAssurePrincipal) => {
				// on remplace l'aff par le contrat associé
				return contrats.find(contrat => contrat.numgar === affiliationAssurePrincipal.numgar)
			})
			.find((_contrat) => {
				// on retourne le premier contrat de base
				return _contrat && _contrat.base
			})
		if (contratBase) {
			const {
				querableNom,
				siret,
				numgar
			} = contratBase
			return {
				name: querableNom,
				siret,
				numgar
			}
		}
	}
})

/**
 * La garantie en vigueur.
 */
export const getGarantieEnVigueur = createSelector(
	[getAffiliationAssurePrincipal],
	(affiliation) => {
		if (affiliation?.garanties?.length > 0) {
			return affiliation.garanties
				.filter(selectGarantieToDisplay)
				.find((_contrat) => _contrat?.base)
		}
		return null
	}
)

export const getContratsSante = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiairesForManage,
	getContratsSanteByCompanies,
	/**
	 * Chaque garantie est lié à un contrat sante.
	 *
	 * On liste l'ensemble des garanties des bénéficiaire
	 * on sépare en deux groupe BASE et OTP
	 * Pour chaque groupe in regroupe les beneficaire de celle-ci selon le nom de la garantie
	 *
	 * Le regroupement retourne la objet {
	 *  base : [
	 *             garantie: la garantie concernée
	 *             beneficiaires : les beneficiaire rattache a cette garantie
	 *          }]
	 *  opt :[{
	 *             garantie: la garantie concernée
	 *             beneficiaires : les beneficiaire rattache a cette garantie
	 *          }]
	 * }
	 *
	 * @param assurePrincipal, l'assuré principal
	 * @param beneficiaires, les bénéficiaires
	 * @param contratsSante, les contrats santé
	 * @returns {*}
	 */
	(assurePrincipal, beneficiaires, contratsSante) => {
		if (!contratsSante || contratsSante.length === 0) {
			return []
		}
		// sans assuré principal, il n'y a rien à afficher
		if (!assurePrincipal) {
			return []
		}
		// récupération des garanties de l'assuré principal
		const affiliationList = [
			assurePrincipal,
			...beneficiaires
		]

		const contrats = {
			'base': [],
			'option': []
		}

		let nomGarantieBase = []
		let nomGarantieOtp = []

		affiliationList.forEach(aff => {
			aff.garanties.filter(g => {
				return selectGarantieToDisplay(g)
			}).forEach(g => {
				if (g.typeGar.indexOf('option') !== -1) {
					const index = nomGarantieOtp.findIndex(gBase => gBase.nomGarantie === g.nomGarantie && gBase.refcie === aff.refCie)
					if (index !== -1) {
						contrats['option'][index].beneficiaires.push(aff)
					} else {
						contrats['option'].push({
							garantie: { ...transformGarantie(g), refcie: aff.refCie },
							beneficiaires: [aff]
						})
						nomGarantieOtp.push({ ...transformGarantie(g), refcie: aff.refCie })
					}
				} else {
					const index = nomGarantieBase.findIndex(gBase => gBase.nomGarantie === g.nomGarantie && gBase.refcie === aff.refCie)
					if (index !== -1) {
						contrats['base'][index].beneficiaires.push(aff)
					} else {
						contrats['base'].push({
							garantie: { ...transformGarantie(g), refcie: aff.refCie },
							beneficiaires: [aff]
						})
						nomGarantieBase.push({ ...transformGarantie(g), refcie: aff.refCie })
					}
				}
			})
		})
		return contrats
	}
)

export const getRangCouv1 = (garanties) => {
	const garantie = garanties
		.filter(g => validateDateFin(g.dateFin))
		.find(g => g.base) ?? garanties.find(g => validateDateFin(g.dateFin))

	return garantie?.rangCouv === 1
}

/**
 * Les garanties (dont expirées) dont le rang couv est à 2
 */
export const getRangCouv2 = (garanties) => {
	const garantie = garanties.find(g => g.base)
		?? garanties.reduce((plusRecente, garantie) => {
			return new Date(garantie.dateDebut) > new Date(plusRecente.dateDebut) ? garantie : plusRecente
		})

	return garantie?.rangCouv === 2
}

/**
 * Les garanties VALIDES dont le rang couv est à 2
 */
export const getRangCouv2Strict = (garanties) => {
	const garantie = garanties
		.filter(g => validateDateFin(g.dateFin))
		.find(g => g.base) ?? garanties.find(g => validateDateFin(g.dateFin))

	return garantie?.rangCouv === 2
}

/**
 * Valider que la date de soin est < à la date de fin de garantie si expirée
 */
export const validDateFinGarantieExpiree = (garanties, date) => {
	const targetDate = new Date(date)

	const garantieValide =
		garanties.find(g => g.base && validateDateFin(g.dateFin)) ??
		garanties.find(g => validateDateFin(g.dateFin))

	if (garantieValide) {
		return true
	}

	const garantiePlusRecente = garanties.reduce((plusRecente, garantie) => {
		return new Date(garantie.dateDebut) > new Date(plusRecente.dateDebut) ? garantie : plusRecente
	})

	return new Date(garantiePlusRecente.dateFin) >= targetDate
}
